import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const readJSON = (k) => { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch { return null; } };
const writeJSON = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* quota */ } };
const removeKey = (k) => { try { localStorage.removeItem(k); } catch { /* ignore */ } };
const errDetail = (e) => {
  const d = e?.response?.data;
  return (d?._detailMeta && !Array.isArray(d._detailMeta)) ? d._detailMeta : d?.detail;
};

// Durable form draft: device copy (instant / offline) + server copy (canonical once synced).
// Only `clear()` (successful submit or explicit Discard) removes it — leaving, app-switching,
// reloads, auth refresh or lost signal never do.
export function useDurableDraft({ scopeKey, value, onRestore, enabled = true, legacyKeys = [] }) {
  const { user } = useAuth();
  const uid = user?._id || user?.id || '';
  const on = !!(enabled && uid && scopeKey);
  const lsKey = `nexflow_fdraft_v1__${uid}__${scopeKey}`;
  const tombKey = `nexflow_fdraft_tomb_v1__${uid}__${scopeKey}`;
  const url = `${API}/form-drafts/${encodeURIComponent(scopeKey || '')}`;

  const [status, setStatus] = useState('idle');
  const [restoredAt, setRestoredAt] = useState(null);
  const meta = useRef({ baseVersion: null, dirty: false });
  const readyRef = useRef(false);
  const clearedRef = useRef(false);
  const lastSer = useRef(null);
  const initialSer = useRef(null);
  const timer = useRef(null);
  const pushing = useRef(false);
  const pending = useRef(false);
  const restoring = useRef(false);
  const syncedRef = useRef(false);      // initial server reconcile finished (or failed offline)
  const epoch = useRef(0);              // bumps on clear() so an in-flight save can't resurrect it
  const clearPromise = useRef(null);
  const onRestoreRef = useRef(onRestore);
  onRestoreRef.current = onRestore;
  const ser = JSON.stringify(value);
  const serRef = useRef(ser);
  serRef.current = ser;

  const writeLocal = useCallback((dataSer, dirty) => {
    writeJSON(lsKey, { data: JSON.parse(dataSer), savedAt: new Date().toISOString(),
      baseVersion: meta.current.baseVersion, dirty });
  }, [lsKey]);

  const apply = useCallback((data, at) => {
    const s = JSON.stringify(data);
    if (s === serRef.current) return;
    restoring.current = true;
    setTimeout(() => { restoring.current = false; }, 60);
    onRestoreRef.current?.(data);
    if (s !== initialSer.current) setRestoredAt(at || new Date().toISOString());
  }, []);

  const adoptServer = useCallback((srv) => {
    meta.current = { baseVersion: srv.version, dirty: false };
    apply(srv.data, srv.updated_at);
    writeLocal(JSON.stringify(srv.data), false);
    setStatus('saved');
  }, [apply, writeLocal]);

  const pushRef = useRef(null);
  const resolveConflict = useCallback((srv) => {
    if (JSON.stringify(srv.data) === serRef.current) {   // same content (e.g. our own keepalive save)
      meta.current = { baseVersion: srv.version, dirty: false };
      setStatus('saved');
      return;
    }
    const when = srv.updated_at ? new Date(srv.updated_at).toLocaleString() : 'recently';
    const keepServer = window.confirm(
      `This draft was also saved from another device or session (${when}).\n\n` +
      'OK = use that newer saved draft\nCancel = keep what is on this device');
    if (keepServer) { adoptServer(srv); return; }
    meta.current = { baseVersion: srv.version, dirty: true };
    pushRef.current?.();
  }, [adoptServer]);

  const push = useCallback(async () => {
    if (!on || !syncedRef.current || clearedRef.current || serRef.current === 'null') return;
    if (pushing.current) { pending.current = true; return; }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) { setStatus('offline'); return; }
    pushing.current = true;
    const sent = serRef.current;
    const ep = epoch.current;
    try {
      if (clearPromise.current) await clearPromise.current;
      if (readJSON(tombKey)) { await axios.delete(url, { withCredentials: true }); removeKey(tombKey); }
      const { data: r } = await axios.put(url, { data: JSON.parse(sent), base_version: meta.current.baseVersion },
        { withCredentials: true });
      if (epoch.current !== ep) { axios.delete(url, { withCredentials: true }).catch(() => {}); return; }
      meta.current.baseVersion = r.version;
      if (serRef.current === sent) { meta.current.dirty = false; writeLocal(sent, false); setStatus('saved'); }
      else pending.current = true;
    } catch (e) {
      const d = errDetail(e);
      if (e?.response?.status === 409 && d?.code === 'DRAFT_CONFLICT' && d.server) resolveConflict(d.server);
      else setStatus('offline');
    } finally {
      pushing.current = false;
      if (pending.current && !clearedRef.current) { pending.current = false; setTimeout(() => pushRef.current?.(), 300); }
    }
  }, [on, url, tombKey, writeLocal, resolveConflict]);
  pushRef.current = push;

  // Load: device copy first (instant), then reconcile with the canonical server copy.
  useEffect(() => {
    readyRef.current = false;
    if (!on) return undefined;
    let cancelled = false;
    clearedRef.current = false;
    syncedRef.current = false;
    meta.current = { baseVersion: null, dirty: false };
    initialSer.current = serRef.current;
    lastSer.current = serRef.current;
    const tomb = readJSON(tombKey);
    let local = readJSON(lsKey);
    if (local && tomb && !(local.savedAt > tomb.at)) local = null;
    if (!local) {
      for (const k of legacyKeys) {
        const raw = readJSON(k);
        if (raw) { local = { data: raw, savedAt: raw.savedAt, baseVersion: null, dirty: true }; removeKey(k); break; }
      }
    }
    if (local?.data != null) {
      meta.current = { baseVersion: local.baseVersion ?? null, dirty: !!local.dirty };
      apply(local.data, local.savedAt);
      if (local.dirty) setStatus('offline');
    }
    readyRef.current = true;
    // Only edits carried over from a PREVIOUS session count as local work in the reconcile —
    // seeds/changes made while the server answer is pending never beat the canonical server copy.
    const localDirty = !!local?.dirty;
    const localSer = local?.data != null ? JSON.stringify(local.data) : null;
    (async () => {
      try {
        if (tomb) { await axios.delete(url, { withCredentials: true }); removeKey(tombKey); }
        const { data: srv } = await axios.get(url, { withCredentials: true });
        if (cancelled || clearedRef.current) return;
        syncedRef.current = true;
        const m = meta.current;
        if (!srv?.exists) { if (m.dirty) pushRef.current?.(); return; }
        if (localDirty && local.baseVersion === srv.version) { m.baseVersion = srv.version; pushRef.current?.(); return; }
        if (localDirty && JSON.stringify(srv.data) !== localSer) { m.baseVersion = srv.version; resolveConflict(srv); return; }
        adoptServer(srv);
      } catch {
        if (cancelled) return;
        syncedRef.current = true;
        setStatus(meta.current.dirty ? 'offline' : 'idle');
      }
    })();
    return () => { cancelled = true; clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, lsKey]);

  // Autosave: device copy immediately, server copy debounced.
  useEffect(() => {
    if (!on || !readyRef.current || ser === lastSer.current) return;
    lastSer.current = ser;
    if (ser === 'null' || ser === undefined) return;
    clearedRef.current = false;   // a new change after clear() starts a fresh draft
    const hasRecord = meta.current.baseVersion != null || !!readJSON(lsKey);
    if (ser === initialSer.current && !hasRecord) return;
    if (restoring.current) { restoring.current = false; writeLocal(ser, meta.current.dirty); return; }
    meta.current.dirty = true;
    writeLocal(ser, true);
    setStatus('saving');
    clearTimeout(timer.current);
    timer.current = setTimeout(() => pushRef.current?.(), 1200);
  }, [on, ser, lsKey, writeLocal]);

  // Retry when signal / foreground returns; best-effort flush when backgrounded.
  useEffect(() => {
    if (!on) return undefined;
    const retry = () => { if (meta.current.dirty && !clearedRef.current) pushRef.current?.(); };
    const onVis = () => {
      if (document.visibilityState === 'visible') { retry(); return; }
      if (!meta.current.dirty || clearedRef.current) return;
      try {
        fetch(url, { method: 'PUT', keepalive: true, credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: JSON.parse(serRef.current), base_version: meta.current.baseVersion }) }).catch(() => {});
      } catch { /* ignore */ }
    };
    window.addEventListener('online', retry);
    document.addEventListener('visibilitychange', onVis);
    const iv = setInterval(retry, 20000);
    return () => { window.removeEventListener('online', retry); document.removeEventListener('visibilitychange', onVis); clearInterval(iv); };
  }, [on, url]);

  // Only for a successful submit or an explicit Discard.
  const clear = useCallback(async () => {
    clearedRef.current = true;
    clearTimeout(timer.current);
    meta.current = { baseVersion: null, dirty: false };
    removeKey(lsKey);
    legacyKeys.forEach(removeKey);
    setRestoredAt(null);
    setStatus('idle');
    epoch.current += 1;
    lastSer.current = serRef.current;
    if (!on) return;
    writeJSON(tombKey, { at: new Date().toISOString() });
    clearPromise.current = axios.delete(url, { withCredentials: true })
      .then(() => removeKey(tombKey)).catch(() => { /* tombstone retried on next save/load */ });
    await clearPromise.current;
    clearPromise.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [on, lsKey, tombKey, url]);

  return { status, restoredAt, clear, dismissRestored: () => setRestoredAt(null) };
}
